import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@material-ui/core";
import DraftsIcon from "@material-ui/icons/Drafts";
import ReplyIcon from "@material-ui/icons/Reply";
import {
	Card,
	CardBody,
	CardHeader,
} from "../../../../_metronic/_partials/controls";
import {
	getContactFormMessagesAdmin,
	markContactFormMessageRead,
	replyContactFormMessage,
} from "../../../../api/contact-form";
import SupportReplyEditor from "../support-messages/SupportReplyEditor";
import "../support-messages/SupportReplyEditor.css";
import { alertError, alertSuccess } from "../../../../utils/logger";
import { shallowEqual, useSelector } from "react-redux";

function formatDate(iso) {
	if (!iso) return "—";
	try {
		return new Date(iso).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" });
	} catch (e) {
		return "—";
	}
}

function defaultReplySubject(interest) {
	const label = String(interest || "").trim();
	if (label) return `Re: Tu consulta sobre ${label}`;
	return "Re: Tu mensaje de contacto en Evenely";
}

function ContactFormTable({ rows, emptyMessage, markingId, onOpenDetail, onMarkRead, variant = "unread" }) {
	return (
		<TableContainer component={Paper} variant="outlined">
			<Table size="small">
				<TableHead>
					<TableRow>
						<TableCell>Fecha</TableCell>
						<TableCell>Contacto</TableCell>
						<TableCell>Empresa</TableCell>
						<TableCell>Interés</TableCell>
						<TableCell>Estado</TableCell>
						<TableCell align="right">Acciones</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{rows.length === 0 ? (
						<TableRow>
							<TableCell colSpan={6} align="center">
								{emptyMessage}
							</TableCell>
						</TableRow>
					) : (
						rows.map((row) => {
							const unread = !row.readByAdmin;
							const replied = Boolean(row.repliedAt);
							return (
								<TableRow
									key={row._id}
									style={{ fontWeight: variant === "unread" ? 600 : 400 }}
								>
									<TableCell>{formatDate(row.createdAt)}</TableCell>
									<TableCell>
										<div>{row.name || "—"}</div>
										<div style={{ fontSize: 12, color: "#64748b" }}>{row.email || "—"}</div>
									</TableCell>
									<TableCell>{row.company || "—"}</TableCell>
									<TableCell
										style={{
											maxWidth: 180,
											overflow: "hidden",
											textOverflow: "ellipsis",
											whiteSpace: "nowrap",
										}}
									>
										{row.interest || "—"}
									</TableCell>
									<TableCell>
										{unread ? (
											<Chip size="small" label="Sin leer" color="secondary" />
										) : replied ? (
											<Chip
												size="small"
												label="Respondido"
												style={{ background: "#dcfce7", color: "#166534" }}
											/>
										) : (
											<Chip size="small" label="Leído" />
										)}
									</TableCell>
									<TableCell align="right">
										<Button size="small" onClick={() => onOpenDetail(row)}>
											Gestionar
										</Button>
										{unread ? (
											<Button
												size="small"
												color="primary"
												startIcon={
													markingId === String(row._id) ? (
														<CircularProgress size={16} />
													) : (
														<DraftsIcon />
													)
												}
												disabled={markingId === String(row._id)}
												onClick={() => onMarkRead(row)}
											>
												Leído
											</Button>
										) : null}
									</TableCell>
								</TableRow>
							);
						})
					)}
				</TableBody>
			</Table>
		</TableContainer>
	);
}

export default function ContactFormMessagesPage() {
	const history = useHistory();
	const location = useLocation();
	const authUser = useSelector((s) => s.authentication?.user, shallowEqual);
	const isAdmin = String(authUser?.role || "").toLowerCase() === "admin";
	const [loading, setLoading] = useState(false);
	const [rows, setRows] = useState([]);
	const [replyFilter, setReplyFilter] = useState("");
	const [emailFilter, setEmailFilter] = useState("");
	const [detail, setDetail] = useState(null);
	const [markingId, setMarkingId] = useState("");
	const [replySubject, setReplySubject] = useState("");
	const [replyBody, setReplyBody] = useState("");
	const [replySending, setReplySending] = useState(false);

	const load = useCallback(async () => {
		if (!isAdmin) return;
		setLoading(true);
		try {
			const res = await getContactFormMessagesAdmin({
				limit: 300,
				replyStatus: replyFilter || undefined,
				emailQ: emailFilter || undefined,
			});
			if (res.status === 200 && Array.isArray(res.data)) setRows(res.data);
			else setRows([]);
		} catch (error) {
			setRows([]);
			alertError({ error, customMessage: "No se pudieron cargar los envíos del formulario." });
		} finally {
			setLoading(false);
		}
	}, [isAdmin, replyFilter, emailFilter]);

	useEffect(() => {
		load();
	}, [load]);

	const openDetail = useCallback((row) => {
		if (!row) return;
		setDetail(row);
		setReplySubject(defaultReplySubject(row.interest));
		setReplyBody("");
	}, []);

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const messageId = String(params.get("message") || "").trim();
		if (!messageId || rows.length === 0) return;
		const row = rows.find((r) => String(r._id) === messageId);
		if (row) openDetail(row);
	}, [location.search, rows, openDetail]);

	const closeDetail = () => {
		setDetail(null);
		setReplySubject("");
		setReplyBody("");
		if (location.search) history.replace("/contact-form-messages");
	};

	const markRead = async (row) => {
		if (!row?._id || row.readByAdmin) return;
		setMarkingId(String(row._id));
		try {
			const res = await markContactFormMessageRead(row._id);
			if (res.status === 200 && res.data) {
				setRows((prev) => prev.map((r) => (String(r._id) === String(row._id) ? res.data : r)));
				if (detail && String(detail._id) === String(row._id)) setDetail(res.data);
			}
		} catch (error) {
			alertError({ error, customMessage: "No se pudo marcar como leído." });
		} finally {
			setMarkingId("");
		}
	};

	const sendReply = async () => {
		if (!detail?._id || replySending) return;
		const subject = String(replySubject || "").trim();
		const body = String(replyBody || "").trim();
		const plain = body.replace(/<[^>]+>/g, " ").replace(/&nbsp;/gi, " ").replace(/\s+/g, " ").trim();
		if (subject.length < 2) {
			alertError({ customMessage: "Escribe un asunto para la respuesta." });
			return;
		}
		if (plain.length < 2) {
			alertError({ customMessage: "Escribe el mensaje de respuesta." });
			return;
		}
		setReplySending(true);
		try {
			const res = await replyContactFormMessage(detail._id, { subject, body });
			const updated = res.data?.data || res.data;
			if (updated) {
				setRows((prev) => prev.map((r) => (String(r._id) === String(detail._id) ? updated : r)));
				setDetail(updated);
			}
			setReplyBody("");
			alertSuccess("Correo enviado con diseño Evenely.");
		} catch (error) {
			alertError({ error, customMessage: "No se pudo enviar la respuesta." });
		} finally {
			setReplySending(false);
		}
	};

	const unreadRows = useMemo(() => rows.filter((r) => !r.readByAdmin), [rows]);
	const readRows = useMemo(() => rows.filter((r) => r.readByAdmin), [rows]);
	const hasActiveFilters = Boolean(replyFilter || emailFilter);

	if (!isAdmin) {
		return (
			<Card>
				<CardHeader title="Formulario contacto (web)" />
				<CardBody>
					<Typography color="textSecondary">Solo los administradores pueden ver esta sección.</Typography>
				</CardBody>
			</Card>
		);
	}

	return (
		<>
			<Card>
				<CardHeader title="Formulario contacto (web)" />
				<CardBody>
					<Button variant="outlined" size="small" onClick={load} disabled={loading} style={{ marginBottom: 16 }}>
						Actualizar
					</Button>

					<div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", marginBottom: 20 }}>
						<FormControl variant="outlined" size="small" style={{ minWidth: 170 }}>
							<InputLabel id="contact-reply-filter-label">Respuesta</InputLabel>
							<Select
								labelId="contact-reply-filter-label"
								label="Respuesta"
								value={replyFilter}
								onChange={(e) => setReplyFilter(e.target.value)}
							>
								<MenuItem value="">Todos</MenuItem>
								<MenuItem value="replied">Contestados</MenuItem>
								<MenuItem value="unreplied">Sin contestar</MenuItem>
							</Select>
						</FormControl>

						<TextField
							size="small"
							variant="outlined"
							label="Buscar nombre o email"
							value={emailFilter}
							onChange={(e) => setEmailFilter(e.target.value)}
							style={{ minWidth: 260 }}
						/>

						{hasActiveFilters ? (
							<Button
								size="small"
								onClick={() => {
									setReplyFilter("");
									setEmailFilter("");
								}}
							>
								Quitar filtros
							</Button>
						) : null}
					</div>

					{loading ? (
						<div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
							<CircularProgress />
						</div>
					) : (
						<>
							<div style={{ marginBottom: 28 }}>
								<Typography variant="h6" style={{ fontWeight: 800, marginBottom: 12 }}>
									Sin leer <Chip size="small" label={unreadRows.length} color="secondary" style={{ marginLeft: 8 }} />
								</Typography>
								<ContactFormTable
									rows={unreadRows}
									emptyMessage={hasActiveFilters ? "No hay envíos sin leer con estos filtros." : "No hay envíos sin leer."}
									markingId={markingId}
									onOpenDetail={openDetail}
									onMarkRead={markRead}
									variant="unread"
								/>
							</div>

							<div>
								<Typography variant="h6" style={{ fontWeight: 800, marginBottom: 12 }}>
									Leídos <Chip size="small" label={readRows.length} style={{ marginLeft: 8 }} />
								</Typography>
								<ContactFormTable
									rows={readRows}
									emptyMessage={hasActiveFilters ? "No hay envíos leídos con estos filtros." : "No hay envíos leídos."}
									markingId={markingId}
									onOpenDetail={openDetail}
									onMarkRead={markRead}
									variant="read"
								/>
							</div>
						</>
					)}
				</CardBody>
			</Card>

			<Dialog open={Boolean(detail)} onClose={closeDetail} maxWidth="lg" fullWidth>
				<DialogTitle>Contacto web · {detail?.name || "—"}</DialogTitle>
				<DialogContent>
					<Typography variant="body2" color="textSecondary" gutterBottom>
						{detail?.email} · {formatDate(detail?.createdAt)}
					</Typography>
					{detail?.company ? (
						<Typography variant="body2" color="textSecondary" gutterBottom>
							Empresa: {detail.company}
						</Typography>
					) : null}
					{detail?.interest ? (
						<Typography variant="body2" color="textSecondary" gutterBottom>
							Interés: {detail.interest}
						</Typography>
					) : null}

					<div style={{ display: "flex", flexWrap: "wrap", gap: 8, margin: "16px 0 20px" }}>
						{detail && !detail.readByAdmin ? (
							<Button variant="outlined" size="small" onClick={() => markRead(detail)}>
								Marcar como leído
							</Button>
						) : null}
					</div>

					<Typography variant="subtitle2" style={{ fontWeight: 800, marginBottom: 8 }}>
						Mensaje
					</Typography>
					<Paper variant="outlined" style={{ padding: "12px 14px", background: "#faf5ff", marginBottom: 20 }}>
						<Typography variant="body1" style={{ whiteSpace: "pre-wrap" }}>
							{detail?.message || ""}
						</Typography>
					</Paper>

					{detail?.repliedAt ? (
						<>
							<Typography variant="subtitle2" style={{ fontWeight: 800, marginBottom: 8 }}>
								Última respuesta enviada · {formatDate(detail.repliedAt)}
							</Typography>
							<Paper variant="outlined" style={{ padding: "12px 14px", marginBottom: 20 }}>
								<Typography variant="body2" color="textSecondary" gutterBottom>
									{detail.lastReplySubject || "—"}
								</Typography>
								{detail.lastReplyBodyHtml ? (
									<div
										className="support-reply-preview"
										dangerouslySetInnerHTML={{ __html: detail.lastReplyBodyHtml }}
									/>
								) : (
									<Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>
										{detail.lastReplyBody || ""}
									</Typography>
								)}
							</Paper>
						</>
					) : null}

					<Divider style={{ margin: "8px 0 20px" }} />

					<Typography variant="subtitle1" style={{ fontWeight: 800, marginBottom: 12 }}>
						Responder por correo
					</Typography>
					<Typography variant="body2" color="textSecondary" style={{ marginBottom: 14 }}>
						Se enviará a <strong>{detail?.email}</strong> con el diseño Evenely.
					</Typography>

					<TextField
						fullWidth
						label="Asunto"
						variant="outlined"
						size="small"
						value={replySubject}
						onChange={(e) => setReplySubject(e.target.value.slice(0, 280))}
						disabled={replySending}
						style={{ marginBottom: 16 }}
					/>
					<SupportReplyEditor
						value={replyBody}
						onChange={setReplyBody}
						disabled={replySending}
						placeholder="Redacta la respuesta."
					/>

					<div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 24 }}>
						<Button onClick={closeDetail} disabled={replySending}>
							Cerrar
						</Button>
						<Button
							variant="contained"
							color="primary"
							startIcon={replySending ? <CircularProgress size={18} color="inherit" /> : <ReplyIcon />}
							disabled={replySending}
							onClick={sendReply}
						>
							Enviar correo
						</Button>
					</div>
				</DialogContent>
			</Dialog>
		</>
	);
}
